import z from 'zod';
import { Resolver } from 'react-hook-form';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useAsyncEmailValidation } from '@mews-commander/form';
import { createAsyncZodFormResolver, LocalizedText, showSuccessAlert, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { PlatformProfilePrivilegesFlag } from '../Common';
import {
    FieldNames,
    parsePlatformProfileCommonFormDataToPayload,
    platformProfilesCommonValidationSchema,
} from '../CommonPlatformProfileForm';
import { PlatformProfilesRole } from '../CommonPlatformProfileForm/RoleField';

export interface NewPlatformProfileFormValues {
    [FieldNames.Emails]: string[];
    [FieldNames.Role]: PlatformProfilesRole;
    [FieldNames.Privileges]: PlatformProfilePrivilegesFlag[];
}

const defaultValues: NewPlatformProfileFormValues = {
    [FieldNames.Emails]: [],
    [FieldNames.Role]: PlatformProfilesRole.Support,
    [FieldNames.Privileges]: [],
};

export const useNewPlatformProfileForm = ({ onClose }: { onClose: () => void }) => {
    const addProfileMutation = useRepositoryMutation('platformProfiles', 'add', {
        invalidateQueries: { platformProfiles: ['getAll'] },
    });

    const resolver = useNewPlatformProfileFormSchema();

    return useFormControls<NewPlatformProfileFormValues>({
        mode: 'onChange',
        defaultValues,
        resolver,
        onSubmit: async (formData) => {
            const payload = formatFormData(formData);
            await addProfileMutation.mutateAsync(payload);
            onClose();
            showSuccessAlert({
                description: <LocalizedText textKey={TextKey.PlatformProfileInvitesSent} />,
            });
        },
    });
};

export const useNewPlatformProfileFormSchema = (): Resolver<NewPlatformProfileFormValues> => {
    const { mutateAsync } = useRepositoryMutation('platformProfiles', 'validateEmails');
    const validateEmails = useAsyncEmailValidation(mutateAsync);

    const zodSchema = z
        .object({
            [FieldNames.Emails]: z.array(z.string().email()),
        })
        .and(platformProfilesCommonValidationSchema);

    return createAsyncZodFormResolver(
        zodSchema.and(
            z.object({
                [FieldNames.Emails]: z.array(z.string().email()).min(1).superRefine(validateEmails),
            })
        )
    );
};

export const formatFormData = (formData: NewPlatformProfileFormValues) => ({
    emails: formData.emails,
    ...parsePlatformProfileCommonFormDataToPayload(formData),
});
